/**
 * Roles for the Better Auth organization plugin (owner, admin, member), extended with the
 * Meetings features. Import this file from BOTH the server auth config and the client.
 * Layer 2 of the four permission layers (docs/PERMISSIONS.md): auth/features.ts names the statement each
 * role-gated feature needs, and worker/src/authz/ checks it with rolePermits() below.
 *
 * server:  organization({ ac, roles: { owner, admin, member } })
 * client:  organizationClient({ ac, roles: { owner, admin, member } })
 */
import { createAccessControl } from "better-auth/plugins/access";
import { defaultStatements, ownerAc, adminAc, memberAc } from "better-auth/plugins/organization/access";

export const statement = {
  ...defaultStatements,
  teamFollow: ["create", "delete"],      // Team marker follows (Following bar)
  alertRule: ["update"],                 // org alert rules ("Alert me when" defaults)
  digest: ["update"],                    // org-wide weekly digest defaults
  note: ["share"],                       // share a note with the team
  teamView: ["publish"],                 // publish a sidebar view for the whole team
  dashboard: ["share", "publicLink"],    // share an Analytics dashboard in the team; "Anyone with the link" (publicLinks)
  teamPosition: ["update"],              // edit the team's positions (Alignment; /api/v2 team.positions.write)
  apiKey: ["create", "revoke"],          // team API keys (owners/admins only, Phase 4): create one for this team; revoke anyone's
} as const;

export const ac = createAccessControl(statement);

export const member = ac.newRole({
  ...memberAc.statements,
  note: ["share"],
  dashboard: ["share"],
  // No apiKey statement (Phase 4): only owners and admins make team keys. Anyone may still revoke their own key.
});

export const admin = ac.newRole({
  ...adminAc.statements,
  teamFollow: ["create", "delete"],
  alertRule: ["update"],
  digest: ["update"],
  note: ["share"],
  teamView: ["publish"],
  dashboard: ["share", "publicLink"],
  teamPosition: ["update"],
  apiKey: ["create", "revoke"],
});

export const owner = ac.newRole({
  ...ownerAc.statements,
  teamFollow: ["create", "delete"],
  alertRule: ["update"],
  digest: ["update"],
  note: ["share"],
  teamView: ["publish"],
  dashboard: ["share", "publicLink"],
  teamPosition: ["update"],
  apiKey: ["create", "revoke"],
});

export const roles = { owner, admin, member };
export type RoleName = keyof typeof roles;

/** Does this org role hold every action in `permission`? Nothing asked: yes. No role (a resident): no. */
export function rolePermits(role: string | null | undefined, permission?: Record<string, string[]> | null): boolean {
  if (!permission || !Object.keys(permission).length) return true;
  const r = role ? (roles as Record<string, { authorize: (p: never) => { success: boolean } }>)[role] : undefined;
  return !!r && r.authorize(permission as never).success;
}
